import { useDispatch } from 'react-redux'
import styled, { css, useTheme } from 'styled-components'
import { CouponProductDetailScheme } from '@/apis/rest/generated/types'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { Image } from '@/components/common/images/Image'
import { Typo, TypoVariant } from '@/components/common/typography'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { S3_FOLDERS_LEGACY, S3_HOST_URL } from '@/constants/legacy/constEnv'
import ROUTES from '@/constants/legacy/constRoutes'
import { LocationEnum } from '@/constants/location.enum'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { useCustomRouter } from '@/containers/hooks'
import { doSetToggleModalLogin } from '@/stores/reduxUI'
import { percentText } from '@/utils/utilNumber'

interface ProductDetailMaxPriceBenefitProps {
  expectedMileage: number
  confirmMileageRate: number
  confirmMileage: number
  imageReviewMileageRate: number
  imageReviewMileage: number
  membershipOrderConfirmDiffMileageRate: number
  membershipOrderConfirmDiffMileage: number
  isMembership: boolean
  isUserLoggedIn: boolean
  maxDiscountBenefitCoupon?: CouponProductDetailScheme
}

const ListStyleIcon = () => {
  const { color } = useTheme()
  return (
    <Typo variant={TypoVariant.Body3Regular} color={color.gray300}>
      ㄴ
    </Typo>
  )
}

export const ProductDetailMaxPriceBenefit = ({
  expectedMileage,
  confirmMileageRate,
  confirmMileage,
  imageReviewMileageRate,
  imageReviewMileage,
  membershipOrderConfirmDiffMileageRate,
  membershipOrderConfirmDiffMileage,
  isMembership,
  isUserLoggedIn,
  maxDiscountBenefitCoupon,
}: ProductDetailMaxPriceBenefitProps) => {
  const { color } = useTheme()
  const dispatch = useDispatch()
  const { push } = useCustomRouter()
  const isShowBenefitPrice = isUserLoggedIn && !!maxDiscountBenefitCoupon
  const maxBenefitAmount = maxDiscountBenefitCoupon?.maxBenefitAmount || 0
  const couponGroup = maxDiscountBenefitCoupon?.couponGroup
  const appliedDiscountAmount = maxDiscountBenefitCoupon?.appliedDiscountAmount || 0
  const handleClickMembershipMileageButton = () => {
    push({
      pathname: ROUTES.MEMBERSHIP.INTRO,
      query: {
        [QueryStringKeyEnum.Location]: LocationEnum.ProductDetailMembershipMileage,
      },
    })
  }

  const handleClickLoginButton = () => {
    dispatch(doSetToggleModalLogin(true))
  }

  return (
    <ProductDetailMaxPriceBenefitWrapper
      className="product-detail-max-price-benefit"
      isUserLoggedIn={isUserLoggedIn}
      isShowBenefitPrice={isShowBenefitPrice}
    >
      {/* 최대 혜택가 영역 */}
      {isShowBenefitPrice && (
        <details className="product-detail-max-price-benefit__benefit" open={true}>
          <summary className="product-detail-max-price-benefit__benefit-header">
            <Typo variant={TypoVariant.Body3Bold} color={color.gray700}>
              최대 혜택가
            </Typo>
            <div className="product-detail-max-price-benefit__benefit-header-right">
              <Typo variant={TypoVariant.Body1SemiBold} color={color.blue500}>
                {maxBenefitAmount.toLocaleString('ko-KR')}원에 구매 가능
              </Typo>
              <div className="product-detail-max-price-benefit__benefit-header-icon">
                <FlatIcon type={IconNameEnum.IcArrowDown} color={color.gray300} size="1.6rem" />
              </div>
            </div>
          </summary>
          <div className="product-detail-max-price-benefit__benefit-content">
            <div className="product-detail-max-price-benefit__benefit-order-coupon">
              <div className="product-detail-max-price-benefit__benefit-order-coupon-left">
                <ListStyleIcon />
                {couponGroup?.discountType === 'AMOUNT' ? (
                  <Typo variant={TypoVariant.Body3Regular} color={color.gray700}>
                    주문쿠폰 사용 시 {couponGroup?.discountAmount.toLocaleString('ko-KR')}원 추가 할인
                  </Typo>
                ) : (
                  <Typo variant={TypoVariant.Body3Regular} color={color.gray700}>
                    주문쿠폰 사용 시 {percentText(couponGroup?.discountRate || 0)} 추가 할인
                  </Typo>
                )}
              </div>
              <div className="product-detail-max-price-benefit__benefit-order-coupon-right">
                <Typo variant={TypoVariant.Body3Regular} color={color.gray700}>
                  -{appliedDiscountAmount.toLocaleString('ko-KR')}원
                </Typo>
              </div>
            </div>
            <div className="product-detail-max-price-benefit__benefit-notice">
              <Typo variant={TypoVariant.Body4Regular} color={color.gray300}>
                최대 혜택가는 확인용으로, 주문 시 직접 적용이 필요합니다
              </Typo>
            </div>
          </div>
        </details>
      )}

      {/*  최대 적립금 영역 */}
      <details className="product-detail-max-price-benefit__mileage" open={!isShowBenefitPrice}>
        <summary className="product-detail-max-price-benefit__mileage-header">
          <Typo variant={TypoVariant.Body3Bold} color={color.gray700}>
            최대 적립금
          </Typo>
          <div className="product-detail-max-price-benefit__mileage-header-right">
            <Typo variant={TypoVariant.Body1SemiBold} color={color.blue500}>
              {expectedMileage.toLocaleString('ko-KR')}원
            </Typo>
            {isShowBenefitPrice && (
              <div className="product-detail-max-price-benefit__mileage-header-icon">
                <FlatIcon type={IconNameEnum.IcArrowDown} color={color.gray300} size="1.6rem" />
              </div>
            )}
          </div>
        </summary>
        <div className="product-detail-max-price-benefit__mileage-content">
          {!isUserLoggedIn && (
            <button
              className="product-detail-max-price-benefit__login-button"
              type="button"
              onClick={handleClickLoginButton}
            >
              <Typo variant={TypoVariant.Body2Medium} color={color.blue500}>
                로그인하고 아래 혜택받기
              </Typo>
            </button>
          )}
          <div className="product-detail-max-price-benefit__mileage-confirm">
            <div className="product-detail-max-price-benefit__mileage-confirm-left">
              <ListStyleIcon />
              <Typo variant={TypoVariant.Body3Regular} color={color.gray700}>
                구매 확정 시{' '}
                <Typo variant={TypoVariant.Body3Regular} color={color.blue500}>
                  {percentText(confirmMileageRate)}
                </Typo>{' '}
                적립
              </Typo>
            </div>
            <div className="product-detail-max-price-benefit__mileage-confirm-right">
              <Typo variant={TypoVariant.Body3Regular} color={color.gray700}>
                +{confirmMileage.toLocaleString('ko-KR')}원
              </Typo>
            </div>
          </div>
          {isUserLoggedIn && isMembership && (
            <div className="product-detail-max-price-benefit__mileage-membership-confirm">
              <div className="product-detail-max-price-benefit__mileage-membership-confirm-left">
                <ListStyleIcon />
                <div className="product-detail-max-price-benefit__logo">
                  <Image
                    src={`${S3_HOST_URL}/${S3_FOLDERS_LEGACY.ASSET_IMAGES}/membership/fitpet_plus_logo.svg`}
                    alt="fitpet_plus_logo_simple"
                  />
                </div>
                <Typo variant={TypoVariant.Body3Regular} color={color.gray700}>
                  추가{' '}
                  <Typo variant={TypoVariant.Body3Regular} color={color.blue500}>
                    {percentText(membershipOrderConfirmDiffMileageRate)}
                  </Typo>{' '}
                  적립
                </Typo>
              </div>
              <div className="product-detail-max-price-benefit__mileage-membership-confirm-right">
                <Typo variant={TypoVariant.Body4Regular} color={color.gray700}>
                  +{membershipOrderConfirmDiffMileage.toLocaleString('ko-KR')}원
                </Typo>
              </div>
            </div>
          )}

          <div className="product-detail-max-price-benefit__mileage-review">
            <div className="product-detail-max-price-benefit__mileage-review-left">
              <ListStyleIcon />
              <Typo variant={TypoVariant.Body3Regular} color={color.gray700}>
                리뷰 작성 시{' '}
                <Typo variant={TypoVariant.Body3Regular} color={color.blue500}>
                  {percentText(imageReviewMileageRate)}
                </Typo>{' '}
                적립
              </Typo>
            </div>
            <div className="product-detail-max-price-benefit__mileage-review-right">
              <Typo variant={TypoVariant.Body3Regular} color={color.gray700}>
                +{imageReviewMileage.toLocaleString('ko-KR')}원
              </Typo>
            </div>
          </div>
          <div className="product-detail-max-price-benefit__mileage-notice">
            <Typo variant={TypoVariant.Body4Regular} color={color.gray300}>
              적립금은 실 구매 금액에 따라 지급됩니다.
            </Typo>
          </div>
        </div>
      </details>
    </ProductDetailMaxPriceBenefitWrapper>
  )
}

const ProductDetailMaxPriceBenefitWrapper = styled.div<{ isUserLoggedIn: boolean; isShowBenefitPrice: boolean }>`
  padding: 0 1.2rem;
  border-radius: 0.8rem;
  border: 1px solid
    ${({
      theme: {
        color: { gray100 },
      },
    }) => gray100};
  .product-detail-max-price-benefit {
    &__benefit,
    &__mileage {
      padding: 1.2rem 0;
      border-bottom: 1px solid
        ${({
          theme: {
            color: { gray70 },
          },
        }) => gray70};
      summary {
        po &::marker,
        &::-webkit-details-marker {
          display: none;
          content: '';
        }
        ${({ isShowBenefitPrice }) =>
          !isShowBenefitPrice &&
          css`
            pointer-events: none;
          `};
      }
      &[open] {
        .product-detail-max-price-benefit__benefit-header-icon,
        .product-detail-max-price-benefit__mileage-header-icon {
          transform: rotate(180deg);
        }
      }
    }
    &__mileage {
      border-bottom: none;
    }
    &__benefit-header,
    &__mileage-header {
      cursor: pointer;
      outline: none;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
    &__benefit-header-right,
    &__mileage-header-right {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 0.8rem;
    }
    &__benefit-content {
    }
    &__benefit-order-coupon,
    &__mileage-confirm,
    &__mileage-membership-confirm,
    &__mileage-review {
      margin-top: 1.2rem;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
    &__mileage-review {
      padding-top: 0.8rem;
      border-top: 0.1rem solid
        ${({
          theme: {
            color: { gray70 },
          },
        }) => gray70};
      ${({ isUserLoggedIn }) =>
        !isUserLoggedIn &&
        css`
          margin: 0;
          border-top: 0;
        `};
    }
    &__mileage-membership-confirm {
      margin-top: 0.8rem;
    }
    &__logo {
      display: block;
      width: 7rem;
      height: 2rem;
    }
    &__benefit-order-coupon-left,
    &__mileage-confirm-left,
    &__mileage-membership-confirm-left,
    &__mileage-review-left {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.4rem;
    }
    &__mileage-membership-confirm-left {
      margin-left: 0.8rem;
    }
    &__benefit-notice,
    &__mileage-notice {
      margin-top: 0.8rem;
    }
    &__membership-mileage-button {
      text-decoration: underline;
      padding: 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.2rem;
      border: none;
      background-color: transparent;
      cursor: pointer;
      outline: none;
    }
    &__login-button {
      width: 100%;
      margin-top: 1.2rem;
      padding: 0.8rem 1.4rem;
      border: none;
      background-color: transparent;
      cursor: pointer;
      outline: none;
      border-radius: 0.8rem;
      background-color: ${({
        theme: {
          color: { blue50 },
        },
      }) => blue50};
    }
  }
`
